/**
 * Localized display helpers for therapy_wiki controlled vocabulary.
 *
 * The database stores canonical English tokens in the base columns and
 * Chinese labels in the *_cn columns, but a few fields (contraindication
 * flags, session format, frequency) are either free text or Chinese-only.
 * These maps keep the UI from leaking raw or wrong-language values.
 */

type Pair = { en: string; zh: string };

const pick = (isZh: boolean, p: Pair) => (isZh ? p.zh : p.en);

/** Contraindication flags — stored as Chinese free text in the DB. */
const FLAG_MAP: Record<string, Pair> = {
  '不能替代医疗（无充分实证，严重身心问题须就医）': {
    en: 'Not a substitute for medical care — seek professional help for serious conditions',
    zh: '不能替代医疗（无充分实证，严重身心问题须就医）',
  },
  '严重精神病性症状（幻觉/妄想/双相躁狂期）慎用': {
    en: 'Use caution with severe psychotic symptoms (hallucinations, delusions, bipolar mania)',
    zh: '严重精神病性症状（幻觉/妄想/双相躁狂期）慎用',
  },
  '严重精神病性症状/易解离者慎用': {
    en: 'Use caution with severe psychotic symptoms or dissociation-prone clients',
    zh: '严重精神病性症状/易解离者慎用',
  },
  '个体差异大，开始前与从业者明确目标、频率与边界': {
    en: 'Responses vary widely — agree on goals, frequency and boundaries with the practitioner first',
    zh: '个体差异大，开始前与从业者明确目标、频率与边界',
  },
  '需医师/精神科评估并监测': {
    en: 'Requires medical or psychiatric assessment and monitoring',
    zh: '需医师/精神科评估并监测',
  },
};

/** Returns localized flag labels, dropping the meaningless "none" marker. */
export const localizeContraindicationFlags = (
  isZh: boolean,
  flags?: string[] | null
): string[] => {
  if (!flags?.length) return [];
  return flags
    .map((f) => (f ?? '').trim())
    .filter((f) => f && f.toLowerCase() !== 'none' && f !== '无')
    .map((f) => {
      const hit = FLAG_MAP[f];
      return hit ? pick(isZh, hit) : f;
    });
};

/** Session format — DB holds Chinese values only. */
const FORMAT_MAP: Record<string, Pair> = {
  '线下为主': { en: 'Primarily in-person', zh: '线下为主' },
  '线上为主': { en: 'Primarily online', zh: '线上为主' },
  '线上线下均可': { en: 'Online or in-person', zh: '线上线下均可' },
  '团体为主': { en: 'Primarily group-based', zh: '团体为主' },
};

export const localizeFormat = (
  isZh: boolean,
  format?: string | null,
  formatEn?: string | null
): string | null => {
  const raw = (format ?? '').trim();
  if (!raw) return (formatEn ?? '').trim() || null;
  const hit = FORMAT_MAP[raw];
  if (hit) return pick(isZh, hit);
  if (!isZh && formatEn) return formatEn;
  return raw;
};

/**
 * Frequency — stored as a bare number ("1", "1-2") or "varies".
 * Renders it as a readable, localized phrase instead of a naked digit.
 */
export const localizeFrequency = (
  isZh: boolean,
  frequency?: string | null
): string | null => {
  const raw = (frequency ?? '').trim();
  if (!raw) return null;
  if (/^varies$/i.test(raw) || raw === '不定') {
    return isZh ? '视情况而定' : 'Varies';
  }
  if (/^[\d]+(\s*[-–]\s*[\d]+)?$/.test(raw)) {
    const normalized = raw.replace(/\s*[-–]\s*/, '–');
    return isZh ? `每周 ${normalized} 次` : `${normalized}× per week`;
  }
  return raw;
};

/**
 * Target domain — canonical four-domain model only:
 * Body 身 / Vitality 灵 / Consciousness 意 / Context 境.
 * Legacy tokens (Spirit, Mind, Social, 气, 心, 社) are folded into the
 * canonical set so no fifth domain can ever reach the UI.
 */
const DOMAIN_ORDER = ['Body', 'Vitality', 'Consciousness', 'Context'] as const;
type DomainToken = (typeof DOMAIN_ORDER)[number];

const DOMAIN_MAP: Record<DomainToken, Pair> = {
  Body: { en: 'Body', zh: '身' },
  Vitality: { en: 'Vitality', zh: '灵' },
  Consciousness: { en: 'Consciousness', zh: '意' },
  Context: { en: 'Context', zh: '境' },
};

/**
 * Canonical domain label from the four boolean columns — the single source of
 * truth. The legacy free-text `target_domain` columns are gone.
 */
export const localizeDomainFlags = (
  isZh: boolean,
  flags: { body?: boolean | null; vitality?: boolean | null; consciousness?: boolean | null; context?: boolean | null }
): string | null => {
  const found: DomainToken[] = [];
  if (flags.body) found.push('Body');
  if (flags.vitality) found.push('Vitality');
  if (flags.consciousness) found.push('Consciousness');
  if (flags.context) found.push('Context');
  const parts = DOMAIN_ORDER.filter((d) => found.includes(d)).map((d) => pick(isZh, DOMAIN_MAP[d]));
  return parts.length ? parts.join(' · ') : null;
};

/** True when a therapy carries at least one canonical domain flag. */
export const hasDomainFlags = (flags: {
  body?: boolean | null;
  vitality?: boolean | null;
  consciousness?: boolean | null;
  context?: boolean | null;
}): boolean => Boolean(flags.body || flags.vitality || flags.consciousness || flags.context);



/** Therapy type — normalized snake_case English tokens. */
const TYPE_MAP: Record<string, Pair> = {
  modality: { en: 'Modality', zh: '疗法' },
  therapy: { en: 'Therapy', zh: '治疗' },
  psychotherapy: { en: 'Psychotherapy', zh: '心理治疗' },
  hypnotherapy: { en: 'Hypnotherapy', zh: '催眠疗法' },
  coaching: { en: 'Coaching', zh: '教练辅导' },
  energy_healing: { en: 'Energy healing', zh: '能量疗法' },
  community_support: { en: 'Community support', zh: '社群支持' },
  program: { en: 'Program', zh: '项目' },
  reading: { en: 'Reading', zh: '解读' },
  self_help: { en: 'Self-help technique', zh: '自助技术' },
};

const titleCase = (v: string) =>
  v.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

export const localizeTherapyType = (
  isZh: boolean,
  type?: string | null,
  typeCn?: string | null
): string | null => {
  const raw = (type ?? '').trim().toLowerCase().replace(/\s+/g, '_');
  if (!raw) return (typeCn ?? '').trim() || null;
  const hit = TYPE_MAP[raw];
  if (hit) return pick(isZh, hit);
  if (isZh && typeCn) return typeCn;
  return titleCase(raw);
};

/** True when the string contains CJK characters. */
export const containsCJK = (value?: string | null): boolean =>
  !!value && /[\u3400-\u9FFF\u3040-\u30FF]/.test(value);

const AGE_GROUP_MAP: Record<string, Pair> = {
  children: { en: 'Children (0–12)', zh: '儿童（0–12 岁）' },
  adolescents: { en: 'Adolescents (13–17)', zh: '青少年（13–17 岁）' },
  adults: { en: 'Adults (18–64)', zh: '成人（18–64 岁）' },
  older_adults: { en: 'Older adults (65+)', zh: '长者（65 岁以上）' },
};

const AGE_GROUP_ORDER = ['children', 'adolescents', 'adults', 'older_adults'];

/** Localized labels for the structured age_groups array, in developmental order. */
export const localizeAgeGroups = (
  isZh: boolean,
  groups?: string[] | null
): string[] => {
  if (!groups || groups.length === 0) return [];
  return AGE_GROUP_ORDER.filter((g) => groups.includes(g)).map((g) =>
    pick(isZh, AGE_GROUP_MAP[g])
  );
};

/** Human-readable age range from min_age / max_age. */
export const formatAgeRange = (
  isZh: boolean,
  minAge?: number | null,
  maxAge?: number | null
): string | null => {
  const hasMin = typeof minAge === 'number';
  const hasMax = typeof maxAge === 'number';
  if (!hasMin && !hasMax) return null;
  if (hasMin && hasMax) return isZh ? `${minAge}–${maxAge} 岁` : `Ages ${minAge}–${maxAge}`;
  if (hasMax) return isZh ? `${maxAge} 岁及以下` : `Up to age ${maxAge}`;
  if (minAge === 0) return isZh ? '全年龄适用' : 'All ages';
  return isZh ? `${minAge} 岁及以上` : `Ages ${minAge}+`;
};

